import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Award, User, ArrowRight, Star, Heart, MapPin, Building, Users, TrendingUp } from 'lucide-react';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

export const CEOMessageSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [leftSectionRef, leftSectionVisible] = useScrollAnimation({ delay: 200 });
  const [rightSectionRef, rightSectionVisible] = useScrollAnimation({ delay: 400 });
  const [messageRef, messageVisible] = useScrollAnimation({ delay: 600 });
  const [sloganRef, sloganVisible] = useScrollAnimation({ delay: 800 });

  return (
    <section id="ceo-message" className="py-24 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-80 h-80 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-green-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-purple-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center mb-20 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-6 border border-blue-200/50">
            <User size={16} className="text-blue-600" />
            <Badge className="bg-transparent text-blue-700 px-0 py-0 text-sm font-semibold">
              CEO Message
            </Badge>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Leadership Vision
            <span 
              className="block bg-clip-text text-transparent transition-all duration-300 hover:scale-105"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundImage = 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundImage = 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))';
              }}
            >
              for Tomorrow
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Hear directly from our CEO about our mission to transform urban infrastructure 
            and create smarter, more efficient cities across Oman and beyond.
          </p>
        </div>

        {/* Enhanced & Attractive CEO Section */}
        <div ref={leftSectionRef} className={`max-w-6xl mx-auto ${createScrollAnimationClasses('fadeInUp', leftSectionVisible)}`}>
          <Card className="group relative overflow-hidden p-8 lg:p-12 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/20 border border-blue-200/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            
            {/* Floating Background Elements */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-blue-400/10 to-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-green-400/10 to-blue-500/10 rounded-full blur-xl"></div>
            <div className="absolute top-1/2 right-1/4 w-12 h-12 bg-gradient-to-br from-purple-400/10 to-pink-500/10 rounded-full blur-lg"></div>
            
            {/* Section 1: Image and Text Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-8">
              
              {/* Enhanced CEO Image */}
              <div className="relative z-10">
                {/* Image Container */}
                <div className="relative">
                  {/* Main Image */}
                  <div className="relative">
                    <img 
                      src={process.env.PUBLIC_URL + "/Website_Work/chairman.png"}
                      alt="V. PAJANISSAMY - CEO of Traffic Service International"
                      className="w-full max-w-xs mx-auto h-50 rounded-2xl border-4 border-white"
                    />
                    
                    {/* Enhanced Name Overlay */}
                    <div 
                      className="absolute bottom-0 left-0 right-0 text-white rounded-b-2xl p-3 backdrop-blur-sm"
                      style={{ 
                        background: 'linear-gradient(to top, rgb(6 47 136), rgba(6, 47, 136, 0.95), rgba(6, 47, 136, 0.8))' 
                      }}
                    >
                      <h3 className="text-lg font-bold group-hover:text-blue-100 transition-colors">V. PAJANISSAMY</h3>
                      <p className="text-xs opacity-90 group-hover:opacity-100 transition-opacity">Chief Executive Officer</p>
                      
                      {/* Decorative Element */}
                      <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <Award size={12} className="text-white" />
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>

              {/* Enhanced CEO Message */}
              <div className="relative z-10 space-y-6">
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 py-2 rounded-full border border-green-200/50">
                    <Star size={14} className="text-green-600" />
                    <span className="text-green-700 text-sm font-semibold">CEO Greetings</span>
                  </div>
                  
                  {/* Decorative Quote Icon */}
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full flex items-center justify-center">
                    <Heart size={12} className="text-blue-600" />
                  </div>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  Welcome to Our Journey
                </h3>

                <div className="space-y-5 text-gray-600">
                  <div className="relative p-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-xl border border-blue-100/50">
                    <div className="absolute top-3 left-3 w-2 h-2 bg-blue-500 rounded-full"></div>
                    <p className="pl-4">
                      Welcome to our company website! As the CEO, I am thrilled to highlight one of our key divisions: 
                      <strong className="text-blue-600"> Traffic Service International (TSI) OMAN</strong>. 
                      Our journey began with a vision to transform urban infrastructure and create smarter, more efficient cities.
                    </p>
                  </div>
                  
                  <div className="relative p-4 bg-gradient-to-br from-green-50/50 to-emerald-50/30 rounded-xl border border-green-100/50">
                    <div className="absolute top-3 left-3 w-2 h-2 bg-green-500 rounded-full"></div>
                    <p className="pl-4">
                      At TSI, our mission is to revolutionize traffic management on a global scale. We specialize in 
                      providing innovative solutions that enhance traffic flow, improve safety, and reduce environmental 
                      impact through advanced technologies and intelligent systems.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Enhanced Commitment */}
            <div ref={messageRef} className={`relative z-10 ${createScrollAnimationClasses('fadeInUp', messageVisible)}`}>
              <div className="border-t border-blue-200/50 pt-8">
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 rounded-full border border-blue-200/50 mb-6">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-blue-700 text-sm font-semibold">Our Commitment</span>
                  </div>
                  
                  {/* Decorative Element */}
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-indigo-400/20 to-blue-500/20 rounded-full flex items-center justify-center">
                    <Award size={12} className="text-indigo-600" />
                  </div>
                </div>
                
                <div className="space-y-5 text-gray-600">
                  <div className="relative p-5 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-xl border border-blue-100/50">
                    <div className="absolute top-4 left-4 w-2 h-2 bg-blue-500 rounded-full"></div>
                    <p className="pl-4">
                      With a focus on sustainability and efficiency, TSI aims to support cities and communities in 
                      overcoming the challenges of congestion and mobility. We are committed to collaborating with 
                      local governments and organizations to implement strategies that improve daily commutes 
                      and foster a more sustainable future.
                    </p>
                  </div>

                  <div className="relative p-5 bg-gradient-to-br from-indigo-50/50 to-purple-50/30 rounded-xl border border-indigo-100/50">
                    <div className="absolute top-4 left-4 w-2 h-2 bg-indigo-500 rounded-full"></div>
                    <p className="pl-4 font-semibold text-blue-600">
                      Thank you for being part of our journey. Together, we can pave the way for a brighter, 
                      more connected future.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Enhanced Slogan */}
            <div ref={sloganRef} className={`relative z-10 mt-8 text-center ${createScrollAnimationClasses('fadeInUp', sloganVisible)}`}>
              <div className="border-t border-blue-200/50 pt-8">
                <div className="relative inline-block">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 rounded-full border border-blue-200/50 mb-6">
                    <MapPin size={16} className="text-blue-600" />
                    <span className="text-blue-700 text-sm font-semibold">Welcome to the era of</span>
                  </div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full flex items-center justify-center">
                    <Star size={12} className="text-blue-600" />
                  </div>
                  <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-br from-purple-400/20 to-pink-500/20 rounded-full"></div>
                </div>
                
                <h2 
                  className="text-3xl lg:text-4xl font-bold bg-clip-text text-transparent"
                  style={{ 
                    backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
                  }}
                >
                  TSI WORLD!
                </h2>
              </div>
            </div>

            {/* Floating Heart Element */}
            <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-red-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Heart size={20} className="text-white" />
              </div>
            </div>
          </Card>
        </div>

      </div>
    </section>
  );
};